import { IonButton, IonIcon, IonText } from '@ionic/react';
import { closeOutline, warningOutline } from 'ionicons/icons';
import React, { useCallback, useEffect, useState } from 'react';
import ClientSupportService, {
  ClientSupport,
} from '../../services/ClientSupport.service';
import ReleaseChannelService from '../../services/ReleaseChannel.service';
import isElectron from '../../utils/isElectron';
import './ClientSupportBanner.scss';

/**
 * Warns when the installed build has fallen outside the window the API
 * supports, which normally means the user rolled back too far from the
 * download archive. Renders nothing when the build is current.
 */
const ClientSupportBanner: React.FC = () => {
  const [support, setSupport] = useState<ClientSupport | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [returning, setReturning] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const result = await ClientSupportService.check();
      if (!cancelled) {
        setSupport(result);
      }
    };

    void check();
    // Re-check on focus so a rollback that just finished installing is caught
    // without waiting for the next launch.
    window.addEventListener('focus', check);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', check);
    };
  }, []);

  const handleReturnToLatest = useCallback(async () => {
    setReturning(true);
    await ReleaseChannelService.installVersion(null);
    setReturning(false);
  }, []);

  if (!support || support.supported || dismissed) {
    return null;
  }

  return (
    <div
      className={`client-support-banner${support.blocked ? ' is-blocked' : ''}`}
      role="alert"
    >
      <IonIcon icon={warningOutline} />
      <IonText className="client-support-message">
        {support.message ??
          'This version is older than the Crate Hackers service supports. Some things may not work until you update.'}
      </IonText>
      {isElectron() && (
        <IonButton
          size="small"
          fill="solid"
          disabled={returning}
          onClick={handleReturnToLatest}
        >
          {returning ? 'Updating…' : 'Update now'}
        </IonButton>
      )}
      {/* A withdrawn build stays on screen: there is no safe way to keep using it. */}
      {!support.blocked && (
        <IonButton
          size="small"
          fill="clear"
          aria-label="Dismiss"
          onClick={() => setDismissed(true)}
        >
          <IonIcon icon={closeOutline} slot="icon-only" />
        </IonButton>
      )}
    </div>
  );
};

export default ClientSupportBanner;
